import React, { useEffect } from "react";
import Ellipses from "../../components/Ellipses";
import BaseButton from "../../components/BaseButton";
import useController from "./controller";
import { useTranslation } from "react-i18next";
import Game from "../../store/Game";
import { observer } from "mobx-react-lite";
import WrapperPage from "../../components/WrapperPage";
import BaseIcon from "../../components/BaseIcon";
import Butterflies from "../../components/Butterflies";
import useAuth from "../../hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../utils/routes.js";
import "./style.scss";
import usePwaPopup from "../../hooks/usePwaPopup";
import { isPWA } from "../../utils/helpers";
import { autorun } from "mobx";
import useModalActions from "../../hooks/useModalActions";
import { MODAL_TYPES } from "../../utils/emitter";
import Extended57IntroModal from "../../modals/Extended57IntroModal";
import trackEvent from "../../utils/analytics";
import PersonalAccount from "../../store/PersonalAccount";
import {
  partnerFinishedExtended,
  hasExtendedProgressData,
} from "../../utils/extendedProgress";

const EXT57_INTRO_SHOWN_KEY = "wepurplay_ext57_intro_shown";

export default observer(function EndGamePage() {
  const { onNext } = useController();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation();
  const { openPwaModal } = usePwaPopup();
  const { openModal } = useModalActions();

  // GA4 «Stats v2»: юзер закончил игру. Шлём ТОЛЬКО при фактическом доигрывании
  // (handleEndGame передаёт state.justCompleted) — повторные входы в завершённую
  // игру, F5 и прямые заходы на /end-game событий не создают; sessionStorage
  // страхует от F5 (history.state переживает перезагрузку).
  useEffect(() => {
    if (!location.state?.justCompleted) return;
    const gaKey = `ga_complete_${location.state?.gameId ?? "na"}_${
      location.state?.setType ?? "BASE"
    }`;
    if (sessionStorage.getItem(gaKey)) return;
    sessionStorage.setItem(gaKey, "1");
    trackEvent("game_complete", {
      event_category: "Game Flow",
      game_name: "Wepurplay Test",
      // симметрично game_start: воронка в GA4 различима по наборам
      set_type: location.state?.setType ?? "BASE",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Первый показ инфо о +57 (ТЗ п.7.5.1): сразу после смахивания 88-й карты,
  // ДО регистрации, один единственный раз. НО не на ветке WantToKnow «Да»
  // (fromWantToKnow): там юзер уже согласился узнать мнение партнёра и идёт
  // регистрироваться — допродажу доп.теста не показываем. +57 остаётся для тех,
  // кто выбрал «Не сейчас» и досвайпал колоду до конца.
  useEffect(() => {
    // item 26: sessionStorage-дубль флага WantToKnow «Да» — на случай, когда на
    // /end-game раньше «Да» ушёл свайпер по таймеру (без location.state).
    // Флаг одноразовый: считываем и сразу удаляем, чтобы он не подавил +57 в
    // следующем легитимном заходе на /end-game в той же вкладке.
    const fromWantToKnowSession = sessionStorage.getItem(
      "wepurplay_from_want_to_know"
    );
    sessionStorage.removeItem("wepurplay_from_want_to_know");
    if (
      !token &&
      !location.state?.fromWantToKnow &&
      !fromWantToKnowSession &&
      !localStorage.getItem(EXT57_INTRO_SHOWN_KEY)
    ) {
      localStorage.setItem(EXT57_INTRO_SHOWN_KEY, "1");
      openModal(MODAL_TYPES.EXT57_INTRO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Реагируем на состояние стора, а не на ссылку массива zones
    const disposer = autorun(() => {
      const currentGame = Game.game;
      const hasPartner = currentGame?.invitedUserId;
      // 7-я зона (personal) — анкеты, в проверке заполненности карточных зон не участвует
      const hasEmptyZones = Game.zones
        .filter((zone) => zone.kind !== "personal")
        .some((zone) => zone.cards.length === 0);
      const isLoading = Game.isZonesLoading;

      if (hasPartner && hasEmptyZones && !isLoading && !isPWA()) {
        openPwaModal(currentGame.id);
      }
    });
    return () => disposer();
  }, []);

  return (
    <div className="endgame-wrapper centering-page">
      <Extended57IntroModal />
      <Ellipses className="endgame-ellipse" countEllipses={1} />
      <Butterflies className="endgame-butterflies" countEllipses={2} />
      <WrapperPage
        loginBtn={!token}
        burgerMenu={!!token}
        backBtn
        onClickBackBtn={() => navigate(ROUTES.PERSONAL_ACCOUNT_PAGE)}
      >
        <>
          {token ? (
            <div className="endgame-wrapper__description">
              {Game.zones
                .filter((zone) => zone.kind !== "personal")
                .some((zone) => zone.cards.length > 0) &&
              // item 14: доиграл +57 в одиночку (партнёр ещё не прошёл свои +57) —
              // не показываем «Ура, результаты» (результаты не изменятся, кроме 88→145),
              // а показываем «ждём партнёра». Гейт только если бэк отдаёт прогресс +57.
              !(
                location.state?.setType === "EXTENDED_57" &&
                hasExtendedProgressData(Game.game) &&
                !partnerFinishedExtended(Game.game, PersonalAccount.user?.id)
              ) ? (
                <>
                  <p className="endgame-wrapper__description__text">
                    {t("endgame_page.description2")}
                  </p>
                  <BaseButton
                    className="endgame-wrapper__description--matchesActive"
                    onClick={onNext}
                  >
                    {t("watch")}
                  </BaseButton>
                </>
              ) : (
                <>
                  <p className="endgame-wrapper__description__text">
                    {t("endgame_page.description1")}
                  </p>
                  <BaseButton className="endgame-wrapper__description--matches">
                    {t("watch")}
                  </BaseButton>
                  <div className="endgame-wrapper__endgame-desc">
                    <BaseIcon name="clock" />
                    {t("endgame_page.awaiting_partner")}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="know-container">
              <div className="know-container__wrapper">
                <div className="know-container__title">
                  {t("modals.want_to_know_matches.title")}
                </div>
                <div className="know-container__buttons">
                  <BaseButton
                    onClick={() =>
                      navigate(ROUTES.REGISTER_PAGE, {
                        state: { from: location.pathname },
                      })
                    }
                  >
                    {t("next")}
                  </BaseButton>
                </div>
              </div>
            </div>
          )}
        </>
      </WrapperPage>
    </div>
  );
});
